import axios from 'axios';
import { 
  SplitData, 
  SplitResponse, 
  AuthCredentials, 
  RegisterCredentials, 
  AuthResponse,
  User, 
  SigningStatus,
  InviteToSignRequest,
  InviteToSignResponse,
  ResendInvitesResponse,
  SelectiveResendResponse,
  UpdateSplitRequest,
  UpdateSplitResponse,
  PublicSplitData,
  SignAgreementRequest,
  SignAgreementResponse,
  SignLinkValidationResult,
  SignSplitRequest,
  SignSplitResponse,
  Split,
  SplitDetails,
  CollaboratorEntity,
  CreateCollaboratorRequest,
  UpdateCollaboratorRequest,
  // Account types
  Account,
  AccountUser,
  CreateAccountRequest,
  UpdateAccountRequest,
  AddAccountMemberRequest,
  AccountsResponse,
  AccountResponse,
  AccountMembersResponse,
  // Collaborator Portal types
  PendingWorksResponse,
  ConfirmWorkRequest,
  ConfirmWorkResponse,
  ConfirmAllWorksRequest,
  ConfirmAllWorksResponse,
  RejectWorkRequest,
  RejectWorkResponse,
} from '../types';

// Configure the base URL for the API
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth endpoints
export const authLogin = async (credentials: AuthCredentials): Promise<AuthResponse> => {
  try {
    const response = await apiClient.post<AuthResponse>('/auth/login', credentials);
    return response.data;
  } catch (error) {
    console.error('Error logging in:', error);
    if (axios.isAxiosError(error) && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to log in');
  }
};

export const authRegister = async (credentials: RegisterCredentials): Promise<AuthResponse> => {
  try {
    const response = await apiClient.post<AuthResponse>('/auth/register', credentials);
    return response.data;
  } catch (error) {
    console.error('Error registering:', error);
    if (axios.isAxiosError(error) && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to register');
  }
};

export const authMe = async (): Promise<User> => {
  try {
    const response = await apiClient.get('/auth/me');
    
    // Handle both possible response formats:
    // 1. Direct user object: { id, email, name, tier, ... }
    // 2. Nested user object: { user: { id, email, name, tier, ... } }
    const userData = response.data.user || response.data;
    
    return userData;
  } catch (error) {
    console.error('Error getting user info:', error);
    throw new Error('Failed to get user info');
  }
};

export const authUpgrade = async (): Promise<User> => {
  try {
    const response = await apiClient.post('/auth/upgrade');
    
    // Handle both possible response formats:
    // 1. Direct user object: { id, email, name, tier, ... }
    // 2. Nested user object: { user: { id, email, name, tier, ... } }
    const userData = response.data.user || response.data;
    
    return userData;
  } catch (error) {
    console.error('Error upgrading account:', error);
    if (axios.isAxiosError(error) && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to upgrade account');
  }
};

// Update user profile
export const updateProfile = async (data: { name?: string; email?: string }): Promise<{ user: User; message?: string }> => {
  try {
    const response = await apiClient.put('/auth/me', data);
    return response.data;
  } catch (error) {
    console.error('Error updating profile:', error);
    if (axios.isAxiosError(error) && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to update profile');
  }
};

// Password management endpoints
export const forgotPassword = async (email: string): Promise<{ message: string }> => {
  try {
    const response = await apiClient.post('/auth/forgot-password', { email });
    return response.data;
  } catch (error) {
    console.error('Error requesting password reset:', error);
    if (axios.isAxiosError(error) && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to request password reset');
  }
};

export const resetPassword = async (token: string, password: string): Promise<{ message: string }> => {
  try {
    const response = await apiClient.post('/auth/reset-password', { token, password });
    return response.data;
  } catch (error) {
    console.error('Error resetting password:', error);
    if (axios.isAxiosError(error) && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to reset password');
  }
};

export const changePassword = async (currentPassword: string, newPassword: string): Promise<{ message: string }> => {
  try {
    const response = await apiClient.post('/auth/change-password', { currentPassword, newPassword });
    return response.data;
  } catch (error) {
    console.error('Error changing password:', error);
    if (axios.isAxiosError(error) && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to change password');
  }
};

// Delete user account (self-deletion via auth)
export const deleteUserAccount = async (): Promise<{ message: string }> => {
  try {
    const response = await apiClient.delete('/auth/me');
    return response.data;
  } catch (error) {
    console.error('Error deleting account:', error);
    if (axios.isAxiosError(error) && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to delete account');
  }
};

// Email verification endpoints
export const verifyEmail = async (token: string): Promise<{ message: string; user: User }> => {
  try {
    const response = await apiClient.get(`/auth/verify-email/${token}`);
    return response.data;
  } catch (error) {
    console.error('Error verifying email:', error);
    if (axios.isAxiosError(error) && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to verify email');
  }
};

export const resendVerificationEmail = async (): Promise<{ message: string }> => {
  try {
    const response = await apiClient.post('/auth/resend-verification');
    return response.data;
  } catch (error) {
    console.error('Error resending verification email:', error);
    if (axios.isAxiosError(error) && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to resend verification email');
  }
};

// Stripe subscription endpoints
export const createCheckoutSession = async (): Promise<{ sessionId: string; url: string }> => {
  try {
    const response = await apiClient.post('/stripe/create-checkout-session', { tier: 'pro' });
    return response.data;
  } catch (error) {
    console.error('Error creating checkout session:', error);
    if (axios.isAxiosError(error) && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to create checkout session');
  }
};

export const createPortalSession = async (): Promise<{ url: string }> => {
  try {
    const response = await apiClient.post('/stripe/create-portal-session');
    return response.data;
  } catch (error) {
    console.error('Error creating portal session:', error);
    if (axios.isAxiosError(error) && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to create portal session');
  }
};

export const getSubscriptionStatus = async (): Promise<{
  tier: 'free' | 'pro';
  subscriptionStatus: string | null;
  subscriptionId: string | null;
  subscriptionEndDate: string | null;
}> => {
  try {
    const response = await apiClient.get('/stripe/subscription-status');
    return response.data;
  } catch (error) {
    console.error('Error getting subscription status:', error);
    throw new Error('Failed to get subscription status');
  }
};

export const verifyCheckoutSession = async (sessionId: string): Promise<{
  success: boolean;
  message: string;
  tier: 'free' | 'pro';
}> => {
  try {
    const response = await apiClient.post('/stripe/verify-session', { sessionId });
    return response.data;
  } catch (error) {
    console.error('Error verifying checkout session:', error);
    if (axios.isAxiosError(error) && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to verify checkout session');
  }
};

// Works endpoints (splits)
export const createSplit = async (splitData: SplitData): Promise<SplitResponse> => {
  try {
    const response = await apiClient.post<SplitResponse>('/works', splitData);
    return response.data;
  } catch (error) {
    console.error('Error creating split:', error);
    
    // Handle specific error messages from the backend
    if (axios.isAxiosError(error) && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    
    throw new Error('Failed to create split');
  }
};

export const getUserSplits = async (accountId?: string): Promise<Split[]> => {
  try {
    const params = accountId ? { accountId } : {};
    const response = await apiClient.get<Split[]>('/works', { params });
    return response.data;
  } catch (error) {
    console.error('Error getting user splits:', error);
    throw new Error('Failed to get splits');
  }
};

export const getSplitById = async (splitId: string): Promise<SplitDetails> => {
  try {
    const response = await apiClient.get<SplitDetails>(`/works/${splitId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching split:', error);
    throw new Error('Failed to fetch split details');
  }
};

export const deleteSplit = async (splitId: string): Promise<void> => {
  try {
    await apiClient.delete(`/works/${splitId}`);
  } catch (error) {
    console.error('Error deleting split:', error);
    throw new Error('Failed to delete split');
  }
};

export const downloadSplitPDF = async (splitId: string): Promise<string> => {
  try {
    const response = await apiClient.get<{ url: string }>(`/works/${splitId}/pdf`);
    return response.data.url;
  } catch (error) {
    console.error('Error downloading PDF:', error);
    throw new Error('Failed to download PDF');
  }
};

// E-signing endpoints
export const getSigningStatus = async (splitId: string): Promise<SigningStatus[]> => {
  try {
    const response = await apiClient.get<SigningStatus[]>(`/works/${splitId}/sign-status`);
    return response.data;
  } catch (error) {
    console.error('Error getting signing status:', error);
    throw new Error('Failed to get signing status');
  }
};

// Invite to sign endpoints
export const inviteToSign = async (inviteRequest: InviteToSignRequest): Promise<InviteToSignResponse> => {
  try {
    
    // Use the same approach as your working reference
    const token = localStorage.getItem('auth_token');
    
    const response = await fetch(`${API_BASE_URL}/invite-to-sign`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        collaboratorName: inviteRequest.collaboratorName,
        collaboratorEmail: inviteRequest.email,
        splitId: inviteRequest.splitId
      })
    });
    
    if (response.ok) {
      const data = await response.json();
      return data;
    } else {
      const error = await response.json();
      console.error('Invite error response:', error);
      throw new Error(error.message || 'Failed to send invite');
    }
  } catch (error) {
    console.error('Error sending invite:', error);
    throw error instanceof Error ? error : new Error('Failed to send invite');
  }
};

// Public signing endpoints (no auth required)
export const validateSignLink = async (token: string): Promise<SignLinkValidationResult> => {
  try {
    const response = await axios.get<PublicSplitData>(`${API_BASE_URL}/sign/${token}`);
    
    if (response.status === 200) {
      return {
        status: 'valid',
        data: response.data
      };
    }
    
    return {
      status: 'error',
      error: 'Unexpected response status'
    };
  } catch (error: any) {
    console.error('Error validating sign link:', error);
    
    if (error.response) {
      const { status, data } = error.response;
      
      if (status === 401 && data?.expired === true) {
        return {
          status: 'expired',
          data: data,
          error: data?.message || 'Invite link has expired'
        };
      } else if (status === 401) {
        return {
          status: 'invalid',
          error: data?.message || 'Invalid or malformed token'
        };
      } else if (status >= 400) {
        return {
          status: 'error',
          error: data?.message || 'Failed to validate link'
        };
      }
    }
    
    return {
      status: 'network_error',
      error: 'Network error. Please check your connection and try again.'
    };
  }
};

export const signAgreement = async (signRequest: SignAgreementRequest): Promise<SignAgreementResponse> => {
  try {
    const response = await axios.post<SignAgreementResponse>(`${API_BASE_URL}/sign/${signRequest.token}`, {
      typedName: signRequest.typedName,
      consent: signRequest.consent
    });
    return response.data;
  } catch (error: any) {
    console.error('Error signing agreement:', error);
    
    if (error.response?.status === 401 && error.response?.data?.expired) {
      throw new Error('EXPIRED_DURING_SIGNING');
    }
    
    throw new Error('Failed to sign agreement');
  }
};

// Enhanced signature function for authenticated users
export const signSplit = async (splitId: string, signRequest: SignSplitRequest): Promise<SignSplitResponse> => {
  try {
    const response = await apiClient.post<SignSplitResponse>(`/works/${splitId}/signature`, {
      typedName: signRequest.typedName,
      consent: signRequest.consent
    });
    return response.data;
  } catch (error) {
    console.error('Error signing split:', error);
    throw new Error('Failed to sign split');
  }
};

// Resend invites endpoint
export const resendInvites = async (splitId: string): Promise<ResendInvitesResponse> => {
  try {
    const response = await apiClient.post<ResendInvitesResponse>(`/works/${splitId}/resend-invites`);
    return response.data;
  } catch (error: any) {
    console.error('Error resending invites:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to resend invites');
  }
};

// Selective resend invites endpoint
export const resendToSelected = async (splitId: string, collaboratorEmails: string[]): Promise<SelectiveResendResponse> => {
  try {
    const response = await apiClient.post<SelectiveResendResponse>(`/works/${splitId}/selective-resend`, {
      collaboratorEmails
    });
    return response.data;
  } catch (error: any) {
    console.error('Error resending selective invites:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to resend invites to selected collaborators');
  }
};

// Update split and resend endpoint
export const updateAndResend = async (splitId: string, updateData: UpdateSplitRequest): Promise<UpdateSplitResponse> => {
  try {
    const response = await apiClient.put<UpdateSplitResponse>(`/works/${splitId}/update-and-resend`, updateData);
    return response.data;
  } catch (error: any) {
    console.error('Error updating split:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to update split');
  }
};

// Collaborator management endpoints
export const getCollaborators = async (accountId?: string): Promise<CollaboratorEntity[]> => {
  try {
    const params = accountId ? { accountId } : {};
    const response = await apiClient.get('/collaborators', { params });
    
    // Handle different response formats
    if (response.data && Array.isArray(response.data.collaborators)) {
      // Wrapped format: { collaborators: [], total: 0 }
      return response.data.collaborators;
    } else if (Array.isArray(response.data)) {
      // Direct array format
      return response.data;
    } else {
      console.error('Unexpected response format:', response.data);
      return [];
    }
  } catch (error) {
    console.error('Error fetching collaborators:', error);
    
    // Check if it's a 404 (endpoint not implemented yet) and return empty array
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      console.warn('Collaborator endpoints not yet implemented on backend, returning empty array');
      return [];
    }
    
    throw new Error('Failed to fetch collaborators');
  }
};

export const createCollaborator = async (data: CreateCollaboratorRequest): Promise<CollaboratorEntity> => {
  try {
    const response = await apiClient.post('/collaborators', data);
    
    // Handle different response formats
    if (response.data && typeof response.data === 'object') {
      // If the response is wrapped, extract the collaborator
      const collaborator = response.data.collaborator || response.data;
      
      // Ensure required fields are present
      if (collaborator && collaborator.id && collaborator.name) {
        return collaborator;
      } else {
        console.error('Invalid collaborator object in response:', collaborator);
        throw new Error('Invalid response format from server');
      }
    } else {
      console.error('Unexpected create response format:', response.data);
      throw new Error('Invalid response format from server');
    }
  } catch (error: any) {
    console.error('Error creating collaborator:', error);
    
    // Check if it's a 404 (endpoint not implemented yet)
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      throw new Error('Collaborator management endpoints are not yet available on the backend. Please check back later.');
    }
    
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to create collaborator');
  }
};

export const updateCollaborator = async (id: string, data: UpdateCollaboratorRequest): Promise<CollaboratorEntity> => {
  try {
    const response = await apiClient.put<CollaboratorEntity>(`/collaborators/${id}`, data);
    return response.data;
  } catch (error: any) {
    console.error('Error updating collaborator:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to update collaborator');
  }
};

export const deleteCollaborator = async (id: string): Promise<void> => {
  try {
    await apiClient.delete(`/collaborators/${id}`);
  } catch (error: any) {
    console.error('Error deleting collaborator:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to delete collaborator');
  }
};

// ==========================================
// Account Management Endpoints
// ==========================================

// Get all accounts for the current user
export const getAccounts = async (): Promise<Account[]> => {
  try {
    const response = await apiClient.get<AccountsResponse>('/accounts');
    return response.data.accounts || [];
  } catch (error) {
    console.error('Error fetching accounts:', error);
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return [];
    }
    throw new Error('Failed to fetch accounts');
  }
};

// Get a single account by ID
export const getAccount = async (accountId: string): Promise<AccountResponse> => {
  try {
    const response = await apiClient.get<AccountResponse>(`/accounts/${accountId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching account:', error);
    if (axios.isAxiosError(error) && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to fetch account');
  }
};

// Create a new account
export const createAccount = async (data: CreateAccountRequest): Promise<AccountResponse> => {
  try {
    const response = await apiClient.post<AccountResponse>('/accounts', data);
    return response.data;
  } catch (error) {
    console.error('Error creating account:', error);
    if (axios.isAxiosError(error) && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to create account');
  }
};

// Update an account
export const updateAccount = async (accountId: string, data: UpdateAccountRequest): Promise<{ account: Account }> => {
  try {
    const response = await apiClient.put<{ account: Account }>(`/accounts/${accountId}`, data);
    return response.data;
  } catch (error) {
    console.error('Error updating account:', error);
    if (axios.isAxiosError(error) && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to update account');
  }
};

// Delete an account
export const deleteAccount = async (accountId: string): Promise<void> => {
  try {
    await apiClient.delete(`/accounts/${accountId}`);
  } catch (error) {
    console.error('Error deleting account:', error);
    if (axios.isAxiosError(error) && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to delete account');
  }
};

// Get members of an account
export const getAccountMembers = async (accountId: string): Promise<AccountUser[]> => {
  try {
    const response = await apiClient.get<AccountMembersResponse>(`/accounts/${accountId}/members`);
    return response.data.members || [];
  } catch (error) {
    console.error('Error fetching account members:', error);
    throw new Error('Failed to fetch account members');
  }
};

// Add a member to an account
export const addAccountMember = async (accountId: string, data: AddAccountMemberRequest): Promise<{ member: AccountUser }> => {
  try {
    const response = await apiClient.post<{ member: AccountUser }>(`/accounts/${accountId}/members`, data);
    return response.data;
  } catch (error) {
    console.error('Error adding account member:', error);
    if (axios.isAxiosError(error) && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to add account member');
  }
};

// Update a member's role
export const updateAccountMember = async (
  accountId: string, 
  memberId: string, 
  role: string
): Promise<{ member: AccountUser }> => {
  try {
    const response = await apiClient.put<{ member: AccountUser }>(
      `/accounts/${accountId}/members/${memberId}`, 
      { role }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating account member:', error);
    if (axios.isAxiosError(error) && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to update account member');
  }
};

// Remove a member from an account
export const removeAccountMember = async (accountId: string, memberId: string): Promise<void> => {
  try {
    await apiClient.delete(`/accounts/${accountId}/members/${memberId}`);
  } catch (error) {
    console.error('Error removing account member:', error);
    if (axios.isAxiosError(error) && error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to remove account member');
  }
};

// Get works for manager view (across all accounts)
export const getManagerWorks = async (): Promise<Split[]> => {
  try {
    // This uses the same endpoint but the backend will scope it to all user's accounts
    const response = await apiClient.get<Split[]>('/works');
    return response.data;
  } catch (error) {
    console.error('Error fetching manager works:', error);
    throw new Error('Failed to fetch works');
  }
};

// Get works for a specific account (artist view)
export const getAccountWorks = async (accountId: string): Promise<Split[]> => {
  try {
    const response = await apiClient.get<Split[]>(`/works?accountId=${accountId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching account works:', error);
    throw new Error('Failed to fetch works');
  }
};

// ============================================================
// Collaborator Portal API (Pending Works Dashboard)
// ============================================================

// Get all pending works for a collaborator (public, no auth required)
export const getPendingWorks = async (token: string): Promise<PendingWorksResponse> => {
  try {
    const response = await apiClient.get<PendingWorksResponse>(`/signing/pending/${token}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching pending works:', error);
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 404) {
        throw new Error('INVALID_TOKEN');
      }
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
    }
    throw new Error('Failed to fetch pending works');
  }
};

// Confirm a single work (public, no auth required)
export const confirmWork = async (token: string, request: ConfirmWorkRequest): Promise<ConfirmWorkResponse> => {
  try {
    const response = await apiClient.post<ConfirmWorkResponse>(`/signing/confirm/${token}`, request);
    return response.data;
  } catch (error) {
    console.error('Error confirming work:', error);
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 404) {
        throw new Error('INVALID_TOKEN');
      }
      if (error.response?.status === 400 && error.response?.data?.error === 'Already confirmed') {
        throw new Error('ALREADY_CONFIRMED');
      }
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
    }
    throw new Error('Failed to confirm work');
  }
};

// Confirm all pending works at once (public, no auth required)
export const confirmAllWorks = async (token: string, request: ConfirmAllWorksRequest): Promise<ConfirmAllWorksResponse> => {
  try {
    const response = await apiClient.post<ConfirmAllWorksResponse>(`/signing/confirm-all/${token}`, request);
    return response.data;
  } catch (error) {
    console.error('Error confirming all works:', error);
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 404) {
        throw new Error('INVALID_TOKEN');
      }
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
    }
    throw new Error('Failed to confirm works');
  }
};

// Reject a single work (public, no auth required)
export const rejectWork = async (token: string, request: RejectWorkRequest): Promise<RejectWorkResponse> => {
  try {
    const response = await apiClient.post<RejectWorkResponse>(`/signing/reject/${token}`, request);
    return response.data;
  } catch (error) {
    console.error('Error rejecting work:', error);
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 404) {
        throw new Error('INVALID_TOKEN');
      }
      if (error.response?.status === 400 && error.response?.data?.error === 'Already processed') {
        throw new Error('ALREADY_PROCESSED');
      }
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
    }
    throw new Error('Failed to reject split');
  }
};

export default apiClient;
